/**
 * Planificador diario: organiza por franjas lo que ya existe (hábitos, tareas,
 * recordatorios y fechas importantes del cerebro). No crea datos propios.
 */
import { addDays, currentWeekStart, isoWeekday, type EngineSettings, type LocalDate, type TodayItem } from '@habitos/engine';
import type { HabitRec, NoteRec, ReminderRec, VacationRec } from './model';
import { liveHabits, todayFor, type HabitIndex } from './selectors';

export type Slot = 'morning' | 'afternoon' | 'evening' | 'anytime';
export const SLOTS: Slot[] = ['morning', 'afternoon', 'evening', 'anytime'];

/** Mañana < 12:00 ≤ tarde < 19:00 ≤ noche. Sin hora: "cuando quieras". */
export function slotOf(time: string | null): Slot {
  if (!time) return 'anytime';
  const h = Number(time.slice(0, 2));
  return h < 12 ? 'morning' : h < 19 ? 'afternoon' : 'evening';
}

export interface AgendaEntry {
  key: string;
  kind: 'habit' | 'task' | 'date';
  title: string;
  time: string | null;
  slot: Slot;
  /** Hecho o resuelto (completado, omitido o en vacaciones). */
  done: boolean;
  /** Fin (HH:MM) cuando hay hora: por duración propia, por la meta en minutos o 30 min estimados. */
  end: string | null;
  /** La duración no la ha indicado el usuario (se asumen 30 min). */
  estimated: boolean;
  overdue: boolean;
  habit?: HabitRec;
  item?: TodayItem;
  note?: NoteRec;
}

export interface AgendaInput {
  habits: HabitRec[];
  reminders: ReminderRec[];
  notes: NoteRec[];
  vacations: VacationRec[];
  idx: HabitIndex;
  settings: EngineSettings;
  day: LocalDate;
  today: LocalDate;
}

export interface Agenda {
  slots: Record<Slot, AgendaEntry[]>;
  pending: number;
  done: number;
  conflicts: Conflict[];
}

/** Primera hora de recordatorio activa para ese día de la semana. */
export function reminderTimeFor(habitId: string, reminders: ReminderRec[], day: LocalDate): string | null {
  const wd = isoWeekday(day);
  const times = reminders
    .filter((r) => r.habitId === habitId && r.enabled && !r.deletedAt && (!r.days || r.days.length === 0 || r.days.includes(wd)))
    .map((r) => r.time)
    .sort();
  return times[0] ?? null;
}

export function buildAgenda(input: AgendaInput): Agenda {
  const { day, today } = input;
  const entries: AgendaEntry[] = [];

  for (const h of liveHabits(input.habits)) {
    if (h.kind === 'task') {
      if (h.archivedAt) continue;
      const isToday = day === today;
      const dueThatDay = h.dueDate === day;
      const overdue = isToday && Boolean(h.dueDate && h.dueDate < today && !h.completedAt);
      const undated = isToday && !h.dueDate && !h.completedAt;
      if (!dueThatDay && !overdue && !undated) continue;
      const time = dueThatDay ? (h.dueTime ?? reminderTimeFor(h.id, input.reminders, day)) : null;
      entries.push({ key: h.id, kind: 'task', title: h.name, time, slot: slotOf(time), done: Boolean(h.completedAt), overdue, habit: h, ...span(h, time, day) });
      continue;
    }
    const item = todayFor(h, input.idx, input.vacations, input.settings, day);
    if (!item?.ue) continue;
    const status = item.ue.status;
    const time = reminderTimeFor(h.id, input.reminders, day);
    const done = status === 'completed' || status === 'skipped' || status === 'vacation';
    entries.push({ key: h.id, kind: 'habit', title: h.name, time, slot: slotOf(time), done, overdue: false, habit: h, item, ...span(h, time, day) });
  }

  for (const n of input.notes) {
    if (n.deletedAt || n.kind !== 'date' || n.date !== day) continue;
    entries.push({ key: n.id, kind: 'date', title: n.title, time: null, slot: 'anytime', done: false, overdue: false, note: n, end: null, estimated: false });
  }

  const slots = Object.fromEntries(SLOTS.map((s) => [s, [] as AgendaEntry[]])) as Record<Slot, AgendaEntry[]>;
  const order = (a: AgendaEntry, b: AgendaEntry) =>
    Number(a.done) - Number(b.done) || (a.time ?? '99').localeCompare(b.time ?? '99') || Number(b.overdue) - Number(a.overdue) || a.title.localeCompare(b.title);
  for (const e of entries.sort(order)) slots[e.slot].push(e);
  const actionable = entries.filter((e) => e.kind !== 'date');
  return { slots, pending: actionable.filter((e) => !e.done).length, done: actionable.filter((e) => e.done).length, conflicts: findConflicts(entries) };
}

/** Duración asumida cuando no hay ninguna otra información. */
export const DEFAULT_MINUTES = 30;

const toMin = (t: string) => Number(t.slice(0, 2)) * 60 + Number(t.slice(3, 5));
const fromMin = (m: number) => {
  const c = Math.min(Math.max(m, 0), 24 * 60 - 1);
  return `${String(Math.floor(c / 60)).padStart(2, '0')}:${String(c % 60).padStart(2, '0')}`;
};

/** Minutos que ocupa: los indicados, o la meta si se mide en minutos. null si no se sabe. */
export function durationOf(h: HabitRec, day: LocalDate): number | null {
  if (h.durationMinutes) return h.durationMinutes;
  const v = [...h.versions].filter((x) => x.effectiveFrom <= day).sort((a, b) => (a.effectiveFrom < b.effectiveFrom ? 1 : -1))[0];
  if (v && v.goalType === 'numeric' && v.unit === 'min' && v.target > 0 && h.kind === 'build') return Math.min(v.target, 720);
  return null;
}

function span(h: HabitRec, time: string | null, day: LocalDate): { end: string | null; estimated: boolean } {
  if (!time) return { end: null, estimated: false };
  const d = durationOf(h, day);
  return { end: fromMin(toMin(time) + (d ?? DEFAULT_MINUTES)), estimated: d === null };
}

export interface Conflict {
  a: string;
  b: string;
}

/** Pares de actividades pendientes con hora cuyos intervalos se solapan. */
export function findConflicts(entries: AgendaEntry[]): Conflict[] {
  const timed = entries.filter((e) => e.time && e.end && !e.done && e.kind !== 'date').sort((x, y) => x.time!.localeCompare(y.time!));
  const out: Conflict[] = [];
  for (let i = 0; i < timed.length; i++) {
    for (let j = i + 1; j < timed.length; j++) {
      if (toMin(timed[j]!.time!) >= toMin(timed[i]!.end!)) break;
      out.push({ a: timed[i]!.key, b: timed[j]!.key });
    }
  }
  return out;
}

/** Primer día de la semana que contiene `day`, según el ajuste de inicio de semana. */
export function weekStartOf(day: LocalDate, settings: EngineSettings): LocalDate {
  const start = currentWeekStart(settings);
  return addDays(day, -((isoWeekday(day) - start + 7) % 7));
}

export interface WeekDay {
  day: LocalDate;
  agenda: Agenda;
  entries: AgendaEntry[];
}

/** Siete días desde `start`, con las mismas reglas que la agenda diaria. */
export function buildWeek(input: Omit<AgendaInput, 'day'> & { start: LocalDate }): WeekDay[] {
  return Array.from({ length: 7 }, (_, i) => {
    const day = addDays(input.start, i);
    const agenda = buildAgenda({ ...input, day });
    return { day, agenda, entries: SLOTS.flatMap((s) => agenda.slots[s]) };
  });
}

export interface TimelineBlock {
  entry: AgendaEntry;
  /** Minutos desde las 00:00. */
  start: number;
  end: number;
  /** Columna dentro de su grupo de solapes y número de columnas del grupo. */
  col: number;
  cols: number;
}

export interface Timeline {
  /** Primera y última hora visibles (enteras). */
  fromHour: number;
  toHour: number;
  blocks: TimelineBlock[];
  /** Lo que no tiene hora: se muestra aparte. */
  untimed: AgendaEntry[];
}

/**
 * Línea de tiempo de un día: bloques con su duración, de 07:00 a 22:00 como mínimo (o lo que
 * haga falta), y los que se solapan repartidos en columnas para que todos se vean.
 */
export function buildTimeline(entries: AgendaEntry[], minHour = 7, maxHour = 22): Timeline {
  const timed = entries
    .filter((e) => e.time && e.end)
    .map((e) => ({ entry: e, start: toMin(e.time!), end: Math.max(toMin(e.end!), toMin(e.time!) + 15) }))
    .sort((a, b) => a.start - b.start || b.end - a.end);
  const blocks: TimelineBlock[] = [];
  // Grupos de bloques que se solapan entre sí (directa o indirectamente).
  let group: TimelineBlock[] = [];
  let groupEnd = -1;
  const flush = () => {
    const cols = Math.max(1, ...group.map((b) => b.col + 1));
    for (const b of group) b.cols = cols;
    blocks.push(...group);
    group = [];
  };
  for (const t of timed) {
    if (t.start >= groupEnd && group.length) flush();
    const taken = new Set(group.filter((b) => b.end > t.start).map((b) => b.col));
    let col = 0;
    while (taken.has(col)) col++;
    group.push({ ...t, col, cols: 1 });
    groupEnd = Math.max(groupEnd, t.end);
  }
  if (group.length) flush();
  const fromHour = Math.min(minHour, ...timed.map((t) => Math.floor(t.start / 60)));
  const toHour = Math.max(maxHour, ...timed.map((t) => Math.ceil(t.end / 60)));
  return { fromHour, toHour: Math.min(24, toHour), blocks, untimed: entries.filter((e) => !e.time) };
}
